// @flow strict
/* eslint-disable no-param-reassign */

import * as React from 'react';
import styles from './styles.scss';
import PageContentBlock from '../../internal/PageContentBlock';
import type { BlockRichText } from '../../internal/types';
import ResponsiveImage from '../../internal/ResponsiveImage';
import VideoCover from '../../internal/VideoCover';

type PropTypes = {|
  highlight: string,
  title: string,
  titleImage?: string,
  subtitle: string,
  children: BlockRichText,
  image: string,
  videoUrl?: string,
|};

function calculateOpacity(weight: number): number {
  if (weight <= 0) {
    return Math.min((Math.abs(weight) / 300) * 2, 1);
  }
  return 0;
}

function calculateTransform(weight: number): number[] {
  if (weight <= 0) {
    const x = Math.abs(weight) / 50;
    return [Math.max(1.2 - x / 10, 1), Math.max(12 - x, 0)];
  }
  return [1.2, 12];
}

const animate = (el: HTMLElement) => {
  const s = el.style;
  const rect = el.getBoundingClientRect();
  const windowHeight =
    window.innerHeight || (document.documentElement && document.documentElement.clientHeight);
  const weight = rect.bottom - rect.height / 2 - windowHeight;

  const transform = calculateTransform(weight);

  s.opacity = calculateOpacity(weight).toString();
  s.transform = `
    scale(${transform[0]}, ${transform[0]})
    translate(0, ${transform[1]}px)
  `;
};

const Header = (props: PropTypes) => {
  const textContainer = React.useRef();

  React.useEffect(() => {
    let unbind;
    if (textContainer.current) {
      const ps = [...textContainer.current.querySelectorAll('p')];

      unbind = window.addEventListener(
        'scroll',
        () => {
          ps.forEach(el => {
            animate(el);
          });
        },
        false,
      );
    }

    return () => {
      if (unbind) unbind();
    };
  }, []);

  return (
    <>
      <PageContentBlock className={styles.header}>
        <div className={styles.background}>
          {props.videoUrl ? (
            <VideoCover url={props.videoUrl} />
          ) : (
            <ResponsiveImage
              className={styles.image}
              src={props.image}
              alt=""
              defaultSize={{ width: 1000 }}
              srcSet={[
                { mediaMaxWidth: 750, width: 750 },
                { mediaMaxWidth: 600, width: 600 },
                { mediaMaxWidth: 400, width: 400 },
              ]}
            />
          )}
        </div>
        <div className={styles.firstViewContainer}>
          <div className={styles.firstView}>
            <span className={styles.highlight}>{props.highlight}</span>
            {props.titleImage && (
              <h1 className={styles.title}>
                <img
                  src={`${props.titleImage}?height=60`}
                  alt={props.title}
                  className={styles.titleImage}
                  width="250"
                  height="60"
                />
                <span className={styles.visuallyhidden}>{props.title}</span>
              </h1>
            )}
            {!props.titleImage && <h1 className={styles.title}>{props.title}</h1>}
            <p className={styles.subtitle}>{props.subtitle}</p>
            <span className={styles.arrow} />
          </div>
        </div>
        <div
          ref={textContainer}
          className={styles.text}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={
            typeof props.children === 'string' ? { __html: props.children } : undefined
          }
        >
          {typeof props.children !== 'string' ? props.children : undefined}
        </div>
      </PageContentBlock>
      <div className="hero-after" id="hero-after" />
    </>
  );
};

export default Header;
